import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { QRCodeSVG } from 'qrcode.react';
import { useAuth } from '../contexts/AuthContext';
import { saveTokens } from '../contexts/AuthContext';
import { tvKey } from './tvKeys';
import { LOGO_URL, isFireTvWrapper, exitTvApp } from './tvAssets';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function TvCodeLogin({ onSwitchToEmail }) {
  const { checkAuth } = useAuth();
  const [code, setCode] = useState(null);
  const [remaining, setRemaining] = useState(0);
  const [error, setError] = useState('');
  const pollRef = useRef(null);
  const countdownRef = useRef(null);
  const intervalSec = useRef(4);
  const generationRef = useRef(0);

  const stopTimers = () => {
    if (pollRef.current) clearInterval(pollRef.current);
    if (countdownRef.current) clearInterval(countdownRef.current);
  };

  const startPairing = useCallback(async () => {
    const generation = ++generationRef.current;
    stopTimers();
    setError('');
    setCode(null);
    try {
      const { data } = await axios.post(`${API}/tv/pair/start`);
      // A newer startPairing() superseded this one while the request was in flight:
      // don't schedule timers that would be orphaned (only the latest call owns pollRef/countdownRef)
      if (generationRef.current !== generation) return;
      setCode(data.code);
      setRemaining(data.expires_in);
      intervalSec.current = data.poll_interval || 4;

      countdownRef.current = setInterval(() => setRemaining(r => Math.max(0, r - 1)), 1000);
      pollRef.current = setInterval(async () => {
        try {
          const res = await axios.get(`${API}/tv/pair/poll`, { params: { code: data.code }, withCredentials: true });
          if (res.data.status === 'completed') {
            stopTimers();
            saveTokens(res.data);
            await checkAuth();
          }
        } catch (err) {
          if (err.response && (err.response.status === 404 || err.response.status === 410)) {
            startPairing();
          }
        }
      }, intervalSec.current * 1000);
    } catch {
      if (generationRef.current === generation) setError('Impossible de générer un code. Réessayez.');
    }
  }, [checkAuth]);

  useEffect(() => {
    startPairing();
    return stopTimers;
  }, [startPairing]);

  useEffect(() => {
    const onKey = (e) => {
      if (tvKey(e) === 'ok') { e.preventDefault(); onSwitchToEmail(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onSwitchToEmail]);

  const mins = Math.floor(remaining / 60);
  const secs = String(remaining % 60).padStart(2, '0');
  const activateUrl = `${window.location.host}/activate`;

  return (
    <div className="tv-fade-in" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%' }} data-testid="tv-code-login">
      <img src={LOGO_URL} alt="Maya+" style={{ height: '4vw', marginBottom: '1vw' }} />
      <p style={{ color: 'rgba(255,255,255,0.6)', marginBottom: '2vw', fontSize: '1.3vw' }}>Connectez cette TV à votre compte</p>

      <p style={{ fontSize: '1.15vw', color: 'rgba(255,255,255,0.75)', marginBottom: '0.6vw' }}>
        1. Sur votre téléphone ou ordinateur, rendez-vous sur :
      </p>
      <p style={{ fontSize: '1.6vw', fontWeight: 700, color: '#E9641A', marginBottom: '1.6vw' }} data-testid="tv-activate-url">{activateUrl}</p>

      <div style={{ display: 'flex', alignItems: 'center', gap: '3vw', marginBottom: '1.8vw' }}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <p style={{ fontSize: '1.15vw', color: 'rgba(255,255,255,0.75)', marginBottom: '1vw' }}>2. Entrez ce code :</p>
          <div style={{ display: 'flex', gap: '0.8vw' }} data-testid="tv-pairing-code">
            {code ? code.split('').map((d, i) => (
              <div key={i} style={{
                width: '4.5vw', height: '6vw', display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.25)', borderRadius: '0.6vw',
                fontSize: '3.4vw', fontWeight: 800, letterSpacing: '0.1vw'
              }}>{d}</div>
            )) : (
              <div style={{ height: '6vw', display: 'flex', alignItems: 'center', color: 'rgba(255,255,255,0.5)', fontSize: '1.4vw' }}>
                {error || 'Génération du code…'}
              </div>
            )}
          </div>
          {code && (
            <p style={{ color: 'rgba(255,255,255,0.45)', fontSize: '1.05vw', marginTop: '1vw' }} data-testid="tv-code-expiry">
              Nouveau code dans {mins}:{secs}
            </p>
          )}
        </div>

        {code && (
          <>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.6vw' }}>
              <div style={{ width: '0.1vw', height: '3.5vw', background: 'rgba(255,255,255,0.2)' }} />
              <span style={{ fontSize: '1vw', color: 'rgba(255,255,255,0.4)', fontWeight: 700 }}>OU</span>
              <div style={{ width: '0.1vw', height: '3.5vw', background: 'rgba(255,255,255,0.2)' }} />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }} data-testid="tv-pairing-qr">
              <div style={{ background: '#fff', padding: '0.7vw', borderRadius: '0.6vw' }}>
                <QRCodeSVG value={`${window.location.origin}/activate?code=${code}`} size={256} style={{ width: '9.5vw', height: '9.5vw', display: 'block' }} />
              </div>
              <p style={{ fontSize: '1vw', color: 'rgba(255,255,255,0.6)', marginTop: '0.8vw', maxWidth: '13vw', textAlign: 'center' }}>
                Scannez avec votre téléphone
              </p>
            </div>
          </>
        )}
      </div>

      <div className="tv-btn tv-focused" onClick={onSwitchToEmail}
        style={{ padding: '0.9vw 2.2vw', fontWeight: 700, fontSize: '1.15vw', background: 'rgba(255,255,255,0.12)', border: '2px solid #fff', borderRadius: '0.5vw', cursor: 'pointer' }}
        data-testid="tv-switch-email-btn">
        Se connecter avec e-mail et mot de passe
      </div>
    </div>
  );
}

function TvEmailLogin({ onSwitchToCode }) {
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [idx, setIdx] = useState(0); // 0 email, 1 password, 2 submit, 3 back to code
  const emailRef = useRef(null);
  const passRef = useRef(null);

  useEffect(() => {
    if (idx === 0) emailRef.current?.focus();
    else if (idx === 1) passRef.current?.focus();
    else { emailRef.current?.blur(); passRef.current?.blur(); }
  }, [idx]);

  const submit = async () => {
    if (busy) return;
    setBusy(true); setError('');
    try {
      await login(email.trim(), password);
    } catch (err) {
      setError(err.response?.data?.detail === 'Invalid email or password' ? 'Email ou mot de passe incorrect' : (err.response?.data?.detail || 'Identifiants incorrects'));
      setBusy(false);
    }
  };

  useEffect(() => {
    const onKey = (e) => {
      const k = tvKey(e);
      if (!k) return;
      if (k === 'back' && !onSwitchToCode) { e.preventDefault(); exitTvApp(); return; }
      if (k === 'down') { e.preventDefault(); setIdx(i => Math.min(onSwitchToCode ? 3 : 2, i + 1)); }
      else if (k === 'up') { e.preventDefault(); setIdx(i => Math.max(0, i - 1)); }
      else if (k === 'ok') {
        if (idx === 2) { e.preventDefault(); submit(); }
        else if (idx === 3 && onSwitchToCode) { e.preventDefault(); onSwitchToCode(); }
        else if (idx < 2) { setTimeout(() => setIdx(idx + 1), 50); }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  return (
    <div className="tv-fade-in" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%' }} data-testid="tv-login">
      <img src={LOGO_URL} alt="Maya+" style={{ height: '4vw', marginBottom: '1.2vw' }} />
      <p style={{ color: 'rgba(255,255,255,0.5)', marginBottom: '2.5vw' }}>Connectez-vous pour continuer</p>
      <div style={{ width: '30vw', display: 'flex', flexDirection: 'column', gap: '1.2vw' }}>
        <input ref={emailRef} type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="Adresse email"
          className={`tv-input ${idx === 0 ? 'tv-focused' : ''}`} style={{ padding: '1vw 1.2vw', fontSize: '1.2vw' }} data-testid="tv-login-email" />
        <input ref={passRef} type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Mot de passe"
          className={`tv-input ${idx === 1 ? 'tv-focused' : ''}`} style={{ padding: '1vw 1.2vw', fontSize: '1.2vw' }} data-testid="tv-login-password" />
        <div className={`tv-btn ${idx === 2 ? 'tv-focused' : ''}`} onClick={submit}
          style={{ padding: '1vw', textAlign: 'center', fontWeight: 700, fontSize: '1.25vw', background: idx === 2 ? '#E9641A' : 'rgba(233,100,26,0.75)', cursor: 'pointer' }}
          data-testid="tv-login-submit">
          {busy ? 'Connexion…' : 'Se connecter'}
        </div>
        {onSwitchToCode && (
        <div className={`tv-btn ${idx === 3 ? 'tv-focused' : ''}`} onClick={onSwitchToCode}
          style={{ padding: '0.8vw', textAlign: 'center', fontWeight: 600, fontSize: '1.1vw', background: 'rgba(255,255,255,0.1)', border: idx === 3 ? '2px solid #fff' : '2px solid transparent', borderRadius: '0.5vw', cursor: 'pointer' }}
          data-testid="tv-switch-code-btn">
          Se connecter avec un code
        </div>
        )}
        {error && <p style={{ color: '#f87171', textAlign: 'center', fontSize: '1.1vw' }} data-testid="tv-login-error">{error}</p>}
      </div>
      {onSwitchToCode && (
      <p style={{ color: 'rgba(255,255,255,0.35)', marginTop: '2.5vw', fontSize: '1vw' }}>
        Pas encore de compte ? Créez-le sur le site web ou l'application mobile Maya+
      </p>
      )}
    </div>
  );
}

export function TvLogin() {
  // Politique Amazon : en mode Fire TV, pas de QR code ni d'URL du site (email/mdp uniquement)
  const [mode, setMode] = useState(isFireTvWrapper ? 'email' : 'code');
  if (isFireTvWrapper) return <TvEmailLogin onSwitchToCode={null} />;
  if (mode === 'code') return <TvCodeLogin onSwitchToEmail={() => setMode('email')} />;
  return <TvEmailLogin onSwitchToCode={() => setMode('code')} />;
}
